import Submission, {SubmissionDef, SubmissionId} from '@/types/Submission';
import {ActionContext, Module} from 'vuex';
import RootState from '@/store/rootState';
import remote from '@/remote';
import SearchFilter from '@/types/SearchFilter';
import {BINARY_FILENAME} from '@/types/defs';
import {ProjectDef, ProjectFile, ProjectId} from '@/types/Project';

export interface LoadedSubmission {
    id: SubmissionId;
    submission: Submission;
    binary: FileContent;
    favorite?: boolean;
}

export interface SubmissionsState {
    list: SubmissionDef[];
    favorites: ProjectId[];
    loaded?: LoadedSubmission;
}

type SubmissionContext = ActionContext<SubmissionsState, RootState>;

const submissions: Module<SubmissionsState, RootState> = {
    namespaced: true,

    state: {
        list: [],
        favorites: [],
    },

    actions: {
        async submitProject({ rootState, dispatch }: SubmissionContext, version: string): Promise<SubmissionId> {
            const project = rootState.project.projectDef!.project;
            const binaryFile: ProjectFile = rootState.project.files!.filter((f) => f.name === BINARY_FILENAME)[0];
            if (binaryFile === undefined) {
                throw new Error('The project does not contain the compiled binary file.');
            }
            const submission: Submission = {
                name: project.name,
                description: project.description,
                arch: project.arch,
                archVersion: project.archVersion,
                genres: [...project.genres],
                language: project.language,
                date: Date.now(),
                projectId: rootState.project.projectDef!.id,
                username: project.username,
                uid: project.uid,
                votes: 0,
                version,
            };
            // project.submissionId will be undefined in the first submission, and filled out afterwards
            const submissionId: SubmissionId = await remote().submitProject(submission, binaryFile.content, project.submissionId);
            const newProjectDef: ProjectDef = {
                id: rootState.project.projectDef!.id,
                project: {
                    ...project,
                    submissionId,
                    lastVersionSubmitted: version,
                },
            };
            await dispatch('project/update', newProjectDef, { root: true });
            return submissionId;
        },

        async search({ commit, rootState }: SubmissionContext, filter: SearchFilter): Promise<void> {
            const [list, favorites] = await Promise.all([
                remote().searchSubmissions(filter),
                rootState.user.loggedIn ? remote().listFavorites(rootState.user.user!.uid) : Promise.resolve([]),
            ]);
            commit('UPDATE_LIST', list);
            commit('UPDATE_FAVORITES', favorites);
        },

        async load({ commit, rootState }: SubmissionContext, submissionId: SubmissionId): Promise<void> {
            const [loaded, favoriteList] = await Promise.all([
                remote().loadSubmission(submissionId),
                rootState.user.loggedIn ? remote().listFavorites(rootState.user.user!.uid) : Promise.resolve([]),
            ]);
            const [submission, binary]: [Submission, FileContent] = loaded;
            let favorite : boolean | undefined;
            if (rootState.user.loggedIn)
                favorite = favoriteList.includes(submissionId);
            commit('UPDATE_LOADED_SUBMISSION', { id: submissionId, submission, binary, favorite } as LoadedSubmission);
        },

        async deleteSubmission(_: SubmissionContext, submissionId: SubmissionId): Promise<void> {
            await remote().deleteSubmission(submissionId);
        },

        async favorite({ commit, rootState }: SubmissionContext, submissionId: SubmissionId): Promise<void> {
            const uid = rootState.user.user!.uid;
            commit('UPVOTE_SUBMISSION', submissionId);
            await remote().upvoteSubmission(uid, submissionId);
        },

        async unfavorite({ commit, rootState }: SubmissionContext, submissionId: SubmissionId): Promise<void> {
            const uid = rootState.user.user!.uid;
            commit('DOWNVOTE_SUBMISSION', submissionId);
            await remote().downvoteSubmission(uid, submissionId);
        },
    },

    mutations: {
        UPDATE_LIST(state: SubmissionsState, list: SubmissionDef[]): void {
            state.list = list;
        },

        UPDATE_FAVORITES(state: SubmissionsState, favorites: SubmissionId[]): void {
            state.favorites = favorites;
        },

        UPDATE_LOADED_SUBMISSION(state: SubmissionsState, loaded: LoadedSubmission): void {
            state.loaded = loaded;
        },

        UPVOTE_SUBMISSION(state: SubmissionsState, submissionId: SubmissionId): void {
            state.favorites = [...state.favorites, submissionId];
            if (state.loaded && state.loaded!.id === submissionId)
                state.loaded!.favorite = true;
        },

        DOWNVOTE_SUBMISSION(state: SubmissionsState, submissionId: SubmissionId): void {
            state.favorites = state.favorites.filter((value) => value !== submissionId);
            if (state.loaded && state.loaded!.id === submissionId)
                state.loaded!.favorite = false;
        },
    },
};

export default submissions;
